import React from 'react';
import {StyleSheet, css} from 'aphrodite';
import {FiArrowRight, FiMapPin, FiUsers} from 'react-icons/fi';

const PLACEHOLDER_IMAGE =
  'https://via.placeholder.com/520x340.png?text=YaBatho+Outlet';

const OutletCard = ({outlet, onClick, badge, selectedTab}) => {
  const isOpen = outlet?.isOpen !== false && outlet?.isAvailable !== false;

  const outletName = outlet?.name || 'YaBatho Outlet';
  const outletAddress = outlet?.address || 'Address not available';
  const cuisineType = outlet?.cuisineType || 'Local restaurant';
  const imageUrl = outlet?.imageUrl || PLACEHOLDER_IMAGE;

  const handleClick = () => {
    if (!isOpen) {
      return;
    }

    onClick?.(outlet);
  };

  return (
    <article
      className={css(styles.card, !isOpen && styles.cardClosed)}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={event => {
        if ((event.key === 'Enter' || event.key === ' ') && isOpen) {
          handleClick();
        }
      }}>
      <div className={css(styles.imageWrapper)}>
        <img src={imageUrl} alt={outletName} className={css(styles.image)} />

        <div className={css(styles.topOverlay)}>
          <span className={css(styles.modeBadge)}>
            {selectedTab || 'Delivery'}
          </span>

          {badge ? <span className={css(styles.badge)}>{badge}</span> : null}
        </div>

        {!isOpen && (
          <div className={css(styles.closedOverlay)}>
            <div className={css(styles.closedContent)}>
              <span className={css(styles.closedIcon)}>⏳</span>
              <strong>Currently closed</strong>
              <small>Please check again soon.</small>
            </div>
          </div>
        )}
      </div>

      <div className={css(styles.info)}>
        <div className={css(styles.titleRow)}>
          <div className={css(styles.nameWrap)}>
            <h3 className={css(styles.name)}>{outletName}</h3>
            <p className={css(styles.cuisine)}>{cuisineType}</p>
          </div>

          <span className={css(styles.status, styles.open)}>
            <span className={css(styles.statusDot)} /> Open
          </span>
        </div>

        <div className={css(styles.addressRow)}>
          <FiMapPin aria-hidden="true" />
          <span>{outletAddress}</span>
        </div>

        <div className={css(styles.footerRow)}>
          <span className={css(styles.localPill)}>
            <FiUsers aria-hidden="true" /> Local outlet
          </span>

          <span className={css(styles.footerText)}>
            View menu <FiArrowRight aria-hidden="true" />
          </span>
        </div>
      </div>
    </article>
  );
};

const styles = StyleSheet.create({
  card: {
    width: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '28px',
    overflow: 'hidden',
    cursor: 'pointer',
    border: '1px solid #E6D8BE',
    boxShadow: '0 12px 30px rgba(30, 30, 30, 0.06)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',

    ':hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 18px 38px rgba(15, 61, 46, 0.12)',
      borderColor: '#C9A14A',
    },
  },

  cardClosed: {
    cursor: 'not-allowed',
    opacity: 0.82,

    ':hover': {
      transform: 'none',
      boxShadow: '0 12px 30px rgba(30, 30, 30, 0.06)',
      borderColor: '#E6D8BE',
    },
  },

  imageWrapper: {
    position: 'relative',
    width: '100%',
    height: '190px',
    overflow: 'hidden',
    backgroundColor: '#F4EBD8',

    '@media (max-width: 520px)': {
      height: '175px',
    },
  },

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },

  topOverlay: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    right: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    zIndex: 5,
  },

  modeBadge: {
    backgroundColor: 'rgba(15, 61, 46, 0.92)',
    color: '#C9A14A',
    border: '1px solid rgba(201, 161, 74, 0.45)',
    borderRadius: '999px',
    padding: '7px 11px',
    fontSize: '11px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  badge: {
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    fontWeight: '900',
    fontSize: '11px',
    padding: '7px 11px',
    borderRadius: '999px',
    border: '1px solid #FFFDF7',
  },

  closedOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(15, 61, 46, 0.76)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 8,
    padding: '18px',
    boxSizing: 'border-box',
  },

  closedContent: {
    backgroundColor: '#FFFDF7',
    border: '1px solid #E6D8BE',
    borderRadius: '22px',
    padding: '16px 18px',
    textAlign: 'center',
    boxShadow: '0 14px 30px rgba(0, 0, 0, 0.18)',

    'strong': {
      display: 'block',
      color: '#0F3D2E',
      fontSize: '15px',
      marginBottom: '4px',
    },

    'small': {
      display: 'block',
      color: '#6E6254',
      fontSize: '12px',
      lineHeight: 1.4,
    },
  },

  closedIcon: {
    display: 'block',
    fontSize: '24px',
    marginBottom: '7px',
  },

  info: {
    padding: '16px',
    boxSizing: 'border-box',
  },

  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '14px',
  },

  nameWrap: {
    minWidth: 0,
    flex: 1,
  },

  name: {
    margin: '0 0 6px',
    color: '#0F3D2E',
    fontSize: '19px',
    lineHeight: 1.18,
    fontWeight: '900',
    letterSpacing: '-0.4px',
  },

  address: {
    margin: 0,
    color: '#6E6254',
    fontSize: '13px',
    lineHeight: 1.5,
  },

  cuisine: {
    margin: 0,
    color: '#8C8170',
    fontSize: '12px',
    fontWeight: '800',
  },

  status: {
    flexShrink: 0,
    borderRadius: '999px',
    padding: '7px 10px',
    fontSize: '11px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },

  open: {
    backgroundColor: '#E9F7EF',
    color: '#0F3D2E',
    border: '1px solid #BFE7CE',
  },

  statusDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    backgroundColor: '#27AE60',
    display: 'inline-block',
    marginRight: '5px',
  },

  addressRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '7px',
    minHeight: '38px',
    marginBottom: '14px',
    color: '#8C8170',
    fontSize: '13px',
    lineHeight: 1.45,

    '> svg': {
      flexShrink: 0,
      marginTop: '2px',
      color: '#C9A14A',
    },
  },

  closed: {
    backgroundColor: '#FFE9E9',
    color: '#D9534F',
    border: '1px solid #F5B8B8',
  },

  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    borderRadius: '18px',
    backgroundColor: '#FFFDF7',
    border: '1px solid #F1E7D2',
    marginBottom: '14px',
  },

  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#0F3D2E',
    fontSize: '13px',
    fontWeight: '800',
  },

  metaIcon: {
    fontSize: '14px',
  },

  metaDivider: {
    width: '1px',
    height: '18px',
    backgroundColor: '#E6D8BE',
  },

  footerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  footerText: {
    color: '#0F3D2E',
    fontSize: '12px',
    fontWeight: '900',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
  },

  localPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '7px 10px',
    borderRadius: '999px',
    backgroundColor: '#F4EBD8',
    color: '#0F3D2E',
    fontSize: '11px',
    fontWeight: '900',
  },

  arrow: {
    width: '34px',
    height: '34px',
    borderRadius: '17px',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '900',
    fontSize: '18px',
  },

  arrowDisabled: {
    backgroundColor: '#F4EBD8',
    color: '#8C8170',
  },
});

export default OutletCard;
